// @givmo/board — where Sign was, on a seat whose Givmo account can't sign for its board
// (CBG-SEC2-L4-MOVE; D-CBG-23 flow (b); the approved frames b1 and b1n).
//
// Another organization's sign-in lock binds the account the seat is on, so Board's verdict for it on
// this board (`another_account_required` on the directory entry) keeps it from every signature-class
// act and from executive-session material (Jonathan's answer 2, 2026-10-07 19:14 PDT: "reading
// continues until the move, with signing and executive-session material closed"). In place of the act
// the page says why, and the two ways on: "Create new account", leading, since most directors have no
// second Givmo account, and "Move to another Givmo account". Both open the move's first step
// (`move-page.tsx`), which starts nothing until its own Continue.
//
// The locking organization is named only where the account's own security read names it (Account
// Security's rule, as the invitation's pointer, frame a1); otherwise "Your organization" (b1n). The
// pointer warms that read through the step-up host as it shows, as Sign does before its choice.
//
// The words are the frames' own for an act, and, for a named executive-session record (review-89's
// P1: its "Try again" could never succeed on such a seat), the same sentence with opening in signing's
// place. Jonathan approved them in copy round CBG-SEC2-L4-COPY1 (2026-10-12 13:49 PDT, "otherwise
// approved"), as written. A seat for someone who isn't on the board says its committee where the
// board's says the board (CBG-FIX106, the FIX104 copy page's row 4.13, approved 2026-10-17).

import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Button, testIdProp } from '@givmo/design-system';
import { useNavigate } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { seatCommitteeNames } from '../../director/director-board';
import { useBoardDirectorHost } from '../../director/director-host';
import { directorBoardFor, useDirectorMe } from '../../director/director-me';
import { isCommitteeOnly } from '../../director/director-wire';
import type { SeatMoveVia } from '../../director/seat-move';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { readHoldsAuthenticator, useLatestStepUpHost } from '../../director/signing-gate';

/**
 * The move's first step for this board, the way the director chose, and the page it was chosen on
 * (`back`, where its "Not now" returns).
 */
export function seatMoveSearch(
  orgRef: string,
  via: SeatMoveVia,
  back?: string,
): Record<string, string> {
  return back === undefined ? { org: orgRef, via } : { org: orgRef, via, back };
}

export interface DirectorMovePointerProps {
  orgRef: string;
  /** What the account can't do here: sign (frames b1, b1n), or open a closed session's record. */
  blocks: 'sign' | 'open';
  testId: string;
}

export function DirectorMovePointer({ orgRef, blocks, testId }: DirectorMovePointerProps) {
  const host = useBoardDirectorHost();
  const stepUpHost = useLatestStepUpHost();
  const navigate = useNavigate();
  const here = useRouterState({ select: (state) => state.location.href });
  const board = directorBoardFor(useDirectorMe().data, orgRef);

  // The organization is named only once the account's own security read says its lock binds.
  useEffect(() => {
    void readHoldsAuthenticator(stepUpHost);
  }, [stepUpHost]);

  const who = host.lockingOrganizationName ?? 'Your organization';
  const start = (via: SeatMoveVia) =>
    void navigate({ to: BOARD_DIRECTOR_PAGES.move, search: seatMoveSearch(orgRef, via, here) });

  return (
    <div className="flex flex-col gap-12" {...testIdProp(testId)}>
      <p
        className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp(`${testId}-line`)}
      >
        {/* Frames b1 and b1n; the record's line is the same sentence. A committee seat's own
            words, CBG-FIX106 (row 4.13). */}
        {isCommitteeOnly(board)
          ? blocks === 'sign'
            ? co.movePointerSign(who, seatCommitteeNames(board))
            : co.movePointerOpen(who, seatCommitteeNames(board))
          : blocks === 'sign'
            ? `${who} manages how you sign in to this Givmo account, so it can’t sign for this board. Move your seat to another Givmo account, and sign from there.`
            : `${who} manages how you sign in to this Givmo account, so it can’t open executive-session material for this board. Move your seat to another Givmo account, and open it from there.`}
      </p>
      <div className="flex flex-col gap-12 sm:flex-row sm:flex-wrap">
        <Button
          appearance="saas"
          tone="primary"
          className="w-full sm:w-auto"
          onClick={() => start('new')}
          testId={`${testId}-create`}
        >
          {/* Jonathan's words, 2026-10-07 19:13 PDT. */}
          Create new account
        </Button>
        <Button
          appearance="saas"
          tone="secondary"
          className="w-full sm:w-auto"
          onClick={() => start('existing')}
          testId={`${testId}-move`}
        >
          {/* Jonathan's words (b1's other button). */}
          Move to another Givmo account
        </Button>
      </div>
    </div>
  );
}
